import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/layout';
import { Button, Icon, IconButton, Progress, Skeleton } from '@chakra-ui/react';
import { MdCheckCircle, MdChevronRight, MdClose, MdMood, MdOutlineWarning, MdRadioButtonUnchecked, MdTaskAlt } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/modal';
import { AuthContext } from '../../auth/lib/authContext';
import useProductsApi from '../../products/lib/useProductsApi';
import usePersonaApi from '../../persona/lib/usePersonaApi';
import useExperienceApi from '../../experiences/lib/useExperienceApi';
import useInsightApi from '../../insight/lib/useInsightApi';
import useTranslation from '../../lib/i18n/useTranslation';
import useOnboardingApi from '../lib/useOnboardingApi';

export default function OnboardingChecklist(props) {
  const { variant = 'full' } = props;
  const { t } = useTranslation();
  const { profile } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const productsApi = useProductsApi();
  const personaApi = usePersonaApi();
  const experienceApi = useExperienceApi();
  const insightApi = useInsightApi();
  const onboardingApi = useOnboardingApi();
  const [isFetching, setIsFetching] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [completedSeenStorageKey, setCompletedSeenStorageKey] = useState(null);
  const [firstPersonaId, setFirstPersonaId] = useState(null);
  const [experienceWithProductId, setExperienceWithProductId] = useState(null);
  const [completion, setCompletion] = useState({
    productCreated: false,
    personaCreated: false,
    experienceWithProductCreated: false,
    twoDifferentInsightsCreated: false,
    insightArchived: false,
  });

  const getProgress = async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!profile?.id) {
        return;
      }
      if (!silent && !hasFetchedOnce) {
        setIsFetching(true);
      }
      const [stateInDb, productList, personaList] = await Promise.all([
        onboardingApi.fetchOnboardingState(profile.id),
        productsApi.fetchProductListByUserId(profile.id),
        personaApi.fetchPersonaListByUserId(profile.id),
      ]);
      setCompletedAt(stateInDb?.completed_at || null);
      setIsHidden(!!stateInDb?.dismissed);
      const hasProduct = !!(productList && productList.length > 0);
      const hasPersona = !!(personaList && personaList.length > 0);
      const firstPersona = hasPersona ? personaList[0].persona : null;
      const newFirstPersonaId = firstPersona ? firstPersona.id : null;
      setFirstPersonaId(newFirstPersonaId);

      let foundExperienceId = null;
      if (hasPersona) {
        for (const personaItem of personaList) {
          const currentPersonaId = personaItem.persona.id;
          const experiences = await experienceApi.fetchExperiencesByPersonaId(currentPersonaId);
          if (!experiences || experiences.length === 0) {
            continue;
          }
          for (const experience of experiences) {
            const experienceId = experience?.experience?.id || experience?.id || null;
            if (!experienceId) {
              continue;
            }
            const linkedProducts = Array.isArray(experience?.products)
              ? experience.products
              : await productsApi.fetchProductsByExperienceId(experienceId);
            if (linkedProducts && linkedProducts.length > 0) {
              foundExperienceId = experienceId;
              break;
            }
          }
          if (foundExperienceId) {
            break;
          }
        }
      }

      setExperienceWithProductId(foundExperienceId);
      const hasExperienceWithProduct = !!foundExperienceId;
      let hasTwoDifferentInsightsInExperience = false;
      let hasArchivedInsightInExperience = false;
      if (foundExperienceId) {
        const insightList = await insightApi.fetchInsightListByExperienceId(foundExperienceId);
        if (insightList && insightList.length > 0) {
          const uniqueTypes = new Set(
            insightList
              .map((insight) => insight.type)
              .filter((type) => !!type)
          );
          hasTwoDifferentInsightsInExperience = (
            uniqueTypes.has('improvement')
            && uniqueTypes.has('negative')
          );
          hasArchivedInsightInExperience = insightList.some((insight) => insight.archived === true);
        }
      }

      setCompletion({
        productCreated: hasProduct,
        personaCreated: hasPersona,
        experienceWithProductCreated: hasExperienceWithProduct,
        twoDifferentInsightsCreated: hasTwoDifferentInsightsInExperience,
        insightArchived: hasArchivedInsightInExperience,
      });

      const currentStep = !hasProduct
        ? 'create_product'
        : !hasPersona
          ? 'create_persona'
          : !hasExperienceWithProduct
            ? 'create_experience'
            : !hasTwoDifferentInsightsInExperience
              ? 'create_two_insights'
              : !hasArchivedInsightInExperience
                ? 'archive_insight'
              : 'completed';
      const hasCompleted = hasProduct
        && hasPersona
        && hasExperienceWithProduct
        && hasTwoDifferentInsightsInExperience
        && hasArchivedInsightInExperience;
      const hasCompletedPersisted = !!stateInDb?.completed_at;
      const shouldSetCompletedAt = hasCompleted && !stateInDb?.completed_at;
      const shouldUpdateCurrentStep = !hasCompletedPersisted && stateInDb?.current_step !== currentStep;

      let updatedOnboardingState = stateInDb;
      if (!stateInDb || shouldSetCompletedAt || shouldUpdateCurrentStep) {
        const updatedState = await onboardingApi.upsertOnboardingState(profile.id, {
          dismissed: !!stateInDb?.dismissed,
          current_step: (hasCompletedPersisted ? 'completed' : currentStep),
          completed_at: (stateInDb?.completed_at || (hasCompleted ? new Date().toISOString() : null)),
        });
        if (updatedState) {
          updatedOnboardingState = updatedState;
          setIsHidden(!!updatedState.dismissed);
          setCompletedAt(updatedState.completed_at || null);
        }
      }
      if ((hasCompleted || hasCompletedPersisted) && updatedOnboardingState?.completed_at) {
        const completedKey = `persona_onboarding_completed_seen_${profile.id}_${updatedOnboardingState.completed_at}`;
        setCompletedSeenStorageKey(completedKey);
        const seenCompletedAt = localStorage.getItem(completedKey);
        if (seenCompletedAt !== 'true') {
          setShowCompletedModal(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
      setHasFetchedOnce(true);
    }
  };

  useEffect(() => {
    getProgress();
  }, [profile?.id, location.pathname]);

  const steps = useMemo(() => {
    return [
      {
        id: 'product',
        title: t('home.onboardingStepProductTitle'),
        description: t('home.onboardingStepProductDesc'),
        done: completion.productCreated,
        actionPath: '/products',
      },
      {
        id: 'persona',
        title: t('home.onboardingStepPersonaTitle'),
        description: t('home.onboardingStepPersonaDesc'),
        done: completion.personaCreated,
        actionPath: '/persona',
      },
      {
        id: 'experience',
        title: t('home.onboardingStepExperienceTitle'),
        description: t('home.onboardingStepExperienceDesc'),
        done: completion.experienceWithProductCreated,
        actionPath: firstPersonaId ? `/persona/${firstPersonaId}` : '/persona',
      },
      {
        id: 'insight',
        title: t('home.onboardingStepTwoInsightsTitle'),
        description: t('home.onboardingStepTwoInsightsDesc'),
        done: completion.twoDifferentInsightsCreated,
        actionPath: experienceWithProductId
          ? `/experiences/${experienceWithProductId}`
          : (firstPersonaId ? `/persona/${firstPersonaId}` : '/persona'),
      },
      {
        id: 'archive',
        title: t('home.onboardingStepArchiveInsightTitle'),
        description: t('home.onboardingStepArchiveInsightDesc'),
        done: completion.insightArchived,
        actionPath: experienceWithProductId
          ? `/experiences/${experienceWithProductId}`
          : (firstPersonaId ? `/persona/${firstPersonaId}` : '/persona'),
      },
    ];
  }, [completion, firstPersonaId, experienceWithProductId, t]);

  const completedCount = steps.filter((step) => step.done).length;
  const totalSteps = steps.length;
  const progressValue = Math.round((completedCount / totalSteps) * 100);
  const nextStep = steps.find((step) => !step.done);
  const currentStepIndex = nextStep ? steps.findIndex((step) => step.id === nextStep.id) : totalSteps - 1;
  const isCompleted = !!completedAt || completedCount === totalSteps;
  const isOnCurrentStepPage = !!(nextStep && location.pathname === nextStep.actionPath);

  useEffect(() => {
    if (!profile?.id || !isOnCurrentStepPage || isCompleted) {
      return;
    }
    const refreshInterval = setInterval(() => {
      getProgress({ silent: true });
    }, 2000);
    return () => {
      clearInterval(refreshInterval);
    };
  }, [profile?.id, isOnCurrentStepPage, isCompleted, location.pathname]);

  if (isFetching) {
    if (variant === 'compact') {
      return null;
    }
    return (
      <Flex px={3} mb={3}>
        <Flex
          w="100%"
          border="1px solid"
          borderColor="divider.grey"
          backgroundColor="white"
          p={3}
          flexDirection="column"
          gap={2}
        >
          <Skeleton height="20px" />
          <Skeleton height="10px" />
          <Skeleton height="10px" />
        </Flex>
      </Flex>
    );
  }

  if (isCompleted) {
    return (
      <>
        <Modal
          isOpen={showCompletedModal}
          onClose={() => {
            if (completedSeenStorageKey) {
              localStorage.setItem(completedSeenStorageKey, 'true');
            }
            setShowCompletedModal(false);
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('home.onboardingCompletedTitle')}</ModalHeader>
            <ModalBody>
              <Flex p={3} flexDirection={"column"} alignItems={"center"} justifyContent={"center"} gap={2}>
                <Icon as={MdMood} color="fill.green" boxSize="40px" />
                <Text>{t('home.onboardingCompletedMessage')}</Text>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="orangeOutline"
                onClick={() => {
                  if (completedSeenStorageKey) {
                    localStorage.setItem(completedSeenStorageKey, 'true');
                  }
                  setShowCompletedModal(false);
                }}
              >
                {t('common.close')}
              </Button>
              <Button
                variant="orangeFill"
                ml={2}
                onClick={() => {
                  if (completedSeenStorageKey) {
                    localStorage.setItem(completedSeenStorageKey, 'true');
                  }
                  setShowCompletedModal(false);
                  navigate('/home');
                }}
              >
                {t('home.onboardingGoToHome')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  if (isHidden) {
    if (variant === 'compact') {
      return null;
    }
    return (
      <Flex px={3} mb={3}>
        <Button
          variant="greyOutline"
          leftIcon={<Icon as={MdTaskAlt} boxSize="16px" />}
          onClick={async () => {
            if (!profile?.id) {
              return;
            }
            const updatedState = await onboardingApi.showOnboarding(profile.id);
            setIsHidden(updatedState ? !!updatedState.dismissed : false);
          }}
        >
          {t('home.resumeOnboarding')}
        </Button>
      </Flex>
    );
  }

  if (variant === 'compact') {
    if (location.pathname === '/home' || !nextStep) {
      return null;
    }
    return (
      <>
        <Flex px={3} py={2} borderBottom="1px solid" borderColor="divider.grey" bg="white" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={2} minW={0}>
            <Icon as={MdTaskAlt} color="text.medium.grey" boxSize="16px" />
            <Text fontSize="14px" color="text.medium.grey">
              {t('home.onboardingCurrentStepPrefix')} {currentStepIndex + 1}/{totalSteps}
            </Text>
            <Text fontSize="14px" noOfLines={1}>
              {nextStep.title}
            </Text>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <Button
              size="sm"
              variant="orangeTransparent"
              onClick={() => {
                setShowSkipModal(true);
              }}
            >
              {t('home.onboardingSkip')}
            </Button>
            <Button
              size="sm"
              variant="orangeFill"
              rightIcon={<MdChevronRight />}
              isDisabled={isOnCurrentStepPage}
              onClick={() => {
                navigate(nextStep.actionPath);
              }}
            >
              {t('home.continueStep')}
            </Button>
          </Flex>
        </Flex>
        <Modal isOpen={showSkipModal} onClose={() => setShowSkipModal(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('home.onboardingSkipConfirmTitle')}</ModalHeader>
            <ModalBody>
              <Box p={3}>
                <Flex
                  border="1px"
                  borderColor="divider.grey"
                  borderRadius="2px"
                  backgroundColor="white"
                  alignItems={'center'}
                  p={3}
                >
                  <Icon
                    as={MdOutlineWarning}
                    color={'text.medium.orange'}
                    boxSize={'18px'}
                    mr={3}
                  />
                  <Text>{t('home.onboardingSkipConfirmMessage')}</Text>
                </Flex>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='orangeOutline'
                onClick={() => setShowSkipModal(false)}
              >{t('common.close')}</Button>
              <Button
                variant="orangeFill"
                ml={2}
                onClick={async () => {
                  if (!profile?.id) {
                    return;
                  }
                  await onboardingApi.upsertOnboardingState(profile.id, {
                    dismissed: true,
                    current_step: 'skipped',
                    completed_at: null,
                  });
                  setShowSkipModal(false);
                  setIsHidden(true);
                }}
              >{t('home.onboardingSkip')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Flex px={3} mb={3}>
      <Flex
        w="100%"
        border="1px solid"
        borderColor="divider.grey"
        backgroundColor="white"
        flexDirection="column"
      >
        <Flex
          px={3}
          py={2}
          borderBottom="1px solid"
          borderColor="divider.grey"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap={2}>
            <Icon as={MdTaskAlt} color="text.medium.grey" boxSize="18px" />
            <Text fontSize="16px">{t('home.onboardingTitle')}</Text>
          </Flex>
          <IconButton
            variant="greyTransparent"
            icon={<Icon as={MdClose} boxSize="18px" />}
            aria-label={t('common.close')}
            onClick={async () => {
              if (!profile?.id) {
                return;
              }
              const updatedState = await onboardingApi.hideOnboarding(profile.id);
              setIsHidden(updatedState ? !!updatedState.dismissed : true);
            }}
          />
        </Flex>
        <Flex p={3} flexDirection="column" gap={3}>
          <Box>
            <Flex mb={2} justifyContent="space-between" alignItems="center">
              <Text color="text.medium.grey">{t('home.onboardingDesc')}</Text>
              <Text color="text.medium.grey">{completedCount}/{totalSteps}</Text>
            </Flex>
            <Progress value={progressValue} colorScheme="green" borderRadius="2px" />
          </Box>
          <Flex flexDirection="column" border="1px solid" borderColor="divider.grey">
            {steps.map((step, index) => {
              const isActive = nextStep && nextStep.id === step.id;
              return (
                <Flex
                  key={step.id}
                  px={3}
                  py={2}
                  borderBottom={index < steps.length - 1 ? '1px solid' : 'none'}
                  borderColor="divider.grey"
                  alignItems="center"
                  justifyContent="space-between"
                  bg={isActive ? 'background.grey' : 'white'}
                >
                  <Flex alignItems="center" gap={2} minW={0}>
                    <Icon
                      as={step.done ? MdCheckCircle : MdRadioButtonUnchecked}
                      color={step.done ? 'fill.green' : 'text.medium.grey'}
                      boxSize="18px"
                    />
                    <Text color={step.done ? 'text.medium.grey' : 'text.dark'} noOfLines={1}>
                      {step.title}
                    </Text>
                  </Flex>
                  {isActive && (
                    <Button
                      variant="orangeFill"
                      size="sm"
                      rightIcon={<MdChevronRight />}
                      isDisabled={isOnCurrentStepPage}
                      onClick={() => {
                        navigate(step.actionPath);
                      }}
                    >
                      {t('home.continueStep')}
                    </Button>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
